import { cn } from '@/lib/utils';

export interface RailSection {
  key: string;
  label: string;
  /** Short state line under the label — a count, a summary, a readiness hint. */
  status?: string;
  icon: React.ReactNode;
  /** Dimmed and unclickable, with `status` explaining why. */
  disabled?: boolean;
}

/**
 * Vertical section navigation, matching the production console's rail so the two
 * detail views read as the same kind of screen.
 *
 * Preferred over horizontal tabs here because the sections carry a status line
 * ("3 characters · 2 locations"), which a tab strip has no room for — and because a
 * long page of unrelated concerns is easier to scan as a list than as a row.
 */
export default function SectionRail({
  title,
  sections,
  activeKey,
  onSelect,
}: {
  title: string;
  sections: RailSection[];
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  return (
    <nav aria-label={title}>
      <div className="mb-3 px-2 text-[10px] font-semibold uppercase tracking-wider text-faint">{title}</div>
      {sections.map((s) => {
        const active = s.key === activeKey;
        return (
          <button
            key={s.key}
            type="button"
            disabled={s.disabled}
            aria-current={active ? 'page' : undefined}
            onClick={() => onSelect(s.key)}
            className={cn(
              'relative flex w-full items-start gap-2.5 rounded-lg py-2.5 pl-3 pr-2 text-left transition-colors',
              s.disabled && 'cursor-not-allowed opacity-50',
              !s.disabled && (active ? 'bg-selected' : 'hover:bg-foreground/[0.04]'),
            )}
          >
            {/* The tint alone is only ~1.3:1 against the page. This bar is the
                signal that actually carries — it survives low contrast and does not
                depend on telling two near-identical background colours apart. */}
            {active && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
            <span className={cn('mt-0.5 shrink-0', active ? 'text-primary' : 'text-faint')}>{s.icon}</span>
            <span className="min-w-0">
              <span
                className={cn('block truncate text-[12.5px] font-medium', active ? 'text-foreground' : 'text-muted-foreground')}
              >
                {s.label}
              </span>
              {s.status && <span className="mt-0.5 block truncate text-[10px] text-faint">{s.status}</span>}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
